/* eslint-disable @typescript-eslint/no-namespace */
import React from 'react'
import { Spacer } from '../Spacer/Spacer'
import { BrandIcon } from '../BrandIcon/BrandIcon'
import { BrandIconId } from '../BrandIcon/BrandIconId.type'
import { BrandIconColor } from '../BrandIcon/BrandIconColor.type'
import { PromoColor } from './PromoColor.type'
import { SpacerSpacing } from '../Spacer/SpacerSpacing.type'
import './style.module.scss'
import xss from 'xss'
import { PromoContentAlignment } from './PromoContentAlignment.type'
import { PromoHeadingColor } from './PromoHeadingColor.type'
import { Link, LinkProps } from '../Link/Link'
import { LinkVariant } from '../../types/LinkVariant.type'
import { brxmRteContentFilter } from '../../lib/helpers/brxmRteContentFilter'

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'promo-video-player': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >
      'promo-video-desc': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >
    }
  }
}

export type PromoProps = {
  backgroundColor?: PromoColor
  isArticle?: string
  content?: string
  heading: string
  headingColor?: PromoHeadingColor
  icon?: BrandIconId
  iconColor?: BrandIconColor
  fill?: boolean
  stroke?: boolean
  image?: string
  imageMobile?: string
  iconImage?: string
  imageBorderColor?: string
  imageDesc?: string
  imageBorder?: string
  imagePosition?: string
  superHeadline?: string
  transcript?: string
  audioTranscript?: string
  componentSpacing?: SpacerSpacing
  analytics?: string
  videoId?: string
  links?: LinkProps[] | LinkProps
  testId?: string
  contentAlignment?: PromoContentAlignment
}

export const headingColorMap: Record<string, string> = {
  [PromoHeadingColor.BERRY]: 'u-textColorBerry',
  [PromoHeadingColor.DARK_MINT]: 'u-textColorDarkMint',
  [PromoHeadingColor.MIDNIGHT_BLUE]: 'u-textColorMidnightBlue',
  [PromoHeadingColor.PURPLE]: 'u-textColorPurple',
  [PromoHeadingColor.NEUTRAL]: 'u-textColorNeutral'
}

export const Promo = ({
  backgroundColor: contentBackgroundColor,
  isArticle,
  content,
  heading,
  headingColor,
  icon,
  iconColor,
  fill,
  stroke,
  image,
  imageMobile,
  iconImage,
  imageBorderColor,
  imageDesc,
  imageBorder,
  imagePosition,
  superHeadline,
  transcript,
  audioTranscript,
  componentSpacing,
  videoId,
  links,
  testId,
  contentAlignment
}: PromoProps) => {
  let backgroundColor, textAlignment, elementAlignment

  switch (contentBackgroundColor) {
    case PromoColor.PURPLE_TINT:
      backgroundColor = 'u-bgColorPurpleLight10'
      break
    case PromoColor.MINT_TINT:
      backgroundColor = 'u-bgColorMintLight10'
      break
    case PromoColor.LIME_TINT:
      backgroundColor = 'u-bgColorLimeLight20'
      break
    case PromoColor.STONE:
      backgroundColor = 'u-bgColorStone'
      break
    case PromoColor.BERRY_TINT:
      backgroundColor = 'u-bgColorBerryLight10'
      break
    case PromoColor.ORANGE_TINT:
      backgroundColor = 'u-bgColorOrangeLight20'
      break
    case PromoColor.TRANSPARENT:
      backgroundColor = 'u-bgColorTransparent'
      break
    default:
      backgroundColor = 'u-bgColorWhite'
  }

  switch (contentAlignment) {
    case PromoContentAlignment.CENTER:
      textAlignment = 'u-textCenter'
      elementAlignment = 'u-flexJustifyCenter'
      break
    case PromoContentAlignment.RIGHT:
      textAlignment = 'u-textRight'
      elementAlignment = 'u-flexJustifyEnd'
      break
    default:
      textAlignment = 'u-textLeft'
      elementAlignment = 'u-flexJustifyStart'
  }

  const promo8ColNoMediaH3Color = headingColor
    ? headingColorMap[headingColor]
    : 'u-textColorBerry'
  const promoLinks = links ? (Array.isArray(links) ? links : [links]) : []
  const isArticleTrue = isArticle === 'true'
  const isSinglePromo = promoLinks.length <= 1
  const hasMultiplePromoLinks = promoLinks.length > 1
  const isPromo8ColNoMedia =
    !image && !imageMobile && !videoId && !hasMultiplePromoLinks

  let baseTypeClass = ''
  if (videoId) {
    baseTypeClass = 'Promo-video-content'
  } else if (hasMultiplePromoLinks) {
    baseTypeClass = 'Promo-multiLinks'
  } else if (!isPromo8ColNoMedia) {
    baseTypeClass = 'Promo-image-content'
  }

  let baseSizeClass = ''
  if (isArticleTrue && isPromo8ColNoMedia) {
    baseSizeClass =
      'Promo--8ColNoMedia u-lg-before2of12 u-lg-after2of12 u-flexJustifyCenter Promo-borderRadius u-paddingTop5gu'
  } else if (isPromo8ColNoMedia) {
    baseSizeClass =
      'Promo--8ColNoMedia u-sizeFull u-flexJustifyCenter Promo-borderRadius u-paddingTop5gu'
  } else if (isArticleTrue && hasMultiplePromoLinks) {
    baseSizeClass = 'u-lg-size4of12 u-md-size4of12 u-sm-flexOrderFirst'
  } else if (isArticleTrue) {
    baseSizeClass = 'u-lg-size4of12'
  } else if (hasMultiplePromoLinks) {
    baseSizeClass = 'u-lg-size6of12 u-md-size6of12 u-sm-flexOrderFirst'
  } else {
    baseSizeClass = 'u-lg-size6of12'
  }

  let baseImagePositionClass = ''
  if (isArticleTrue && imagePosition === 'right') {
    baseImagePositionClass = hasMultiplePromoLinks
      ? 'u-lg-before2of12 u-md-before2of12 Promo-borderRadiusLeft'
      : 'u-lg-before2of12 Promo-borderRadiusLeft'
  } else if (isArticleTrue && imagePosition === 'left') {
    baseImagePositionClass = 'Promo-borderRadiusRight'
  }

  const promoBaseClass =
    `${baseTypeClass} ${baseSizeClass} ${baseImagePositionClass}`.trim()

  const getContentClasses = (
    imgPosition?: string,
    imgBgColor?: string,
    hasBorder?: string,
    hasVideo?: boolean
  ): string => {
    const isLeft = imgPosition === 'left'
    const isRight = imgPosition === 'right'
    const isMatchBg = imgBgColor === 'matchbackground'
    const hasBorderTrue = hasBorder === 'true'

    const add = (condition: boolean, ...cls: string[]) => (condition ? cls : [])

    return [
      ...add(
        isSinglePromo,
        'u-sm-flexOrderFirst',
        'u-md-flexOrderFirst',
        'Promo-borderRadiusTop'
      ),
      ...add(isLeft, 'u-flexOrderFirst'),
      ...add(hasBorderTrue, 'u-border'),
      ...add(
        isMatchBg && isLeft,
        'u-flexOrderFirst',
        'u-paddingLeft5gu',
        'u-paddingTop5gu',
        'u-paddingBottom5gu',
        'u-md-paddingBottom0',
        'u-md-paddingHoriz5gu',
        'u-sm-paddingBottom0',
        'u-sm-paddingHoriz5gu'
      ),
      ...add(
        isMatchBg && isRight,
        'u-paddingRight5gu',
        'u-paddingTop5gu',
        'u-paddingBottom5gu',
        'u-md-paddingHoriz5gu',
        'u-md-paddingBottom0',
        'u-sm-paddingHoriz5gu',
        'u-sm-paddingBottom0'
      ),
      ...add(isArticleTrue && isSinglePromo, 'u-lg-size4of12'),
      ...add(
        isArticleTrue && hasMultiplePromoLinks,
        'u-lg-size4of12',
        'u-md-size4of12'
      ),
      ...add(!isArticleTrue && !!hasVideo, 'u-lg-size6of12', 'u-bgColorBlack'),
      ...add(
        !isArticleTrue && !hasVideo && hasMultiplePromoLinks,
        'u-lg-size6of12',
        'u-md-size6of12'
      ),
      ...add(!isArticleTrue && !hasVideo && isSinglePromo, 'u-lg-size6of12')
    ].join(' ')
  }

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className="Promo-redesign u-sizeConstrained"
        data-analytics-container="Promo"
      >
        <div className="u-flex u-flexAlignItemsStretch Grid u-flexAlignItemsCenter Promo-borderRadius">
          <div
            data-testid={testId ? `${testId}-content` : null}
            className={`Promo-content u-paddingHoriz5gu ${backgroundColor} ${promoBaseClass}
            ${!isPromo8ColNoMedia ? 'u-flexAlignContentCenter' : ''}`}
          >
            <div className={!isPromo8ColNoMedia ? 'u-paddingTop6gu' : ''}>
              {iconImage && (
                <img
                  src={`${iconImage}`}
                  alt=""
                  className={`Promo-customIcon${
                    isPromo8ColNoMedia ? ` u-flex ${elementAlignment}` : ''
                  }`}
                  data-testid={testId ? `${testId}-customIcon` : null}
                />
              )}
              {icon && (
                <div
                  className={`${
                    hasMultiplePromoLinks || isPromo8ColNoMedia
                      ? `u-paddingBottom4gu u-flex ${elementAlignment}`
                      : ''
                  }`}
                  data-testid={testId ? `${testId}-icon-container` : null}
                >
                  <BrandIcon
                    icon={icon}
                    color={iconColor ? iconColor : BrandIconColor.NONE}
                    fill={fill ? true : false}
                    stroke={stroke ? true : false}
                    testId={testId ? `${testId}-icon` : undefined}
                  />
                </div>
              )}
              {superHeadline && (
                <p
                  className={`Type-5 u-textEyebrow u-paddingTop4gu u-paddingBottom2gu ${
                    isPromo8ColNoMedia ? `${textAlignment}` : ''
                  }`}
                  data-testid={testId ? `${testId}-superHeadline` : null}
                >
                  {superHeadline}
                </p>
              )}
              {!videoId ? (
                <>
                  {heading && (
                    <h3
                      className={
                        (hasMultiplePromoLinks
                          ? 'Promo-heading--multiLinks'
                          : '') +
                        (isPromo8ColNoMedia
                          ? `${promo8ColNoMediaH3Color} u-textBold ${textAlignment}`
                          : '') +
                        ' u-paddingBottom2gu'
                      }
                      data-testid={testId ? `${testId}-heading` : null}
                      dangerouslySetInnerHTML={{ __html: xss(heading) }}
                    ></h3>
                  )}
                  {content && (
                    <div
                      className={`Content u-paddingBottom6gu ${
                        isPromo8ColNoMedia ? `${textAlignment}` : ''
                      }`}
                      data-testid={testId ? `${testId}-body` : null}
                      dangerouslySetInnerHTML={{
                        __html: brxmRteContentFilter(content ?? '')
                      }}
                    ></div>
                  )}
                </>
              ) : (
                <promo-video-desc
                  className="video-container Content"
                  playlist-id={videoId}
                  data-analytics-playlist-id={videoId}
                ></promo-video-desc>
              )}
              {transcript && (
                <a
                  className="Link u-block u-marginBottom6gu"
                  href={'/binaries' + transcript}
                >
                  Download transcript
                </a>
              )}
              {audioTranscript && (
                <a
                  className="Link u-block u-marginBottom6gu"
                  href={'/binaries' + audioTranscript}
                >
                  Download audio transcript
                </a>
              )}
              {promoLinks.length == 1 && promoLinks[0].url && (
                <div
                  className={`${
                    isPromo8ColNoMedia ? `u-flex ${elementAlignment}` : ''
                  }`}
                >
                  <Link
                    variant={
                      promoLinks[0].color === 'white'
                        ? LinkVariant.SECONDARY
                        : LinkVariant.PRIMARY
                    }
                    name={promoLinks[0].name}
                    url={promoLinks[0].url}
                    target={promoLinks[0].target}
                    analyticsName={promoLinks[0].analyticsName}
                    testId={testId ? `${testId}-button` : undefined}
                    className={`Button u-marginBottom6gu`}
                    ariaLabel={promoLinks[0].ariaLabel}
                  />
                </div>
              )}
            </div>
          </div>
          {!isPromo8ColNoMedia && (
            <div
              className={`u-flexAlignContentCenter Promo-content ${getContentClasses(
                imagePosition,
                imageBorderColor,
                imageBorder,
                !!videoId
              )} ${backgroundColor} ${
                videoId
                  ? 'Promo-video-content'
                  : hasMultiplePromoLinks
                  ? 'Promo-multiLinks'
                  : 'Promo-image-content'
              } ${
                isArticleTrue && imagePosition === 'left'
                  ? 'u-lg-before2of12 Promo-borderRadiusLeft'
                  : ''
              }
            ${
              (isArticleTrue &&
                (imagePosition === 'right' || hasMultiplePromoLinks)) ||
              imagePosition === 'right'
                ? 'Promo-borderRadiusRight'
                : ''
            }
            ${imagePosition === 'left' ? 'Promo-borderRadiusLeft' : ''}`}
            >
              {image && (
                <img
                  className="Promo-image u-sm-hidden"
                  src={image}
                  data-testid={testId ? `${testId}-image` : null}
                  alt={imageDesc ?? ''}
                />
              )}
              {imageMobile && (
                <img
                  className="Promo-image u-md-hidden u-lg-hidden"
                  src={imageMobile}
                  data-testid={testId ? `${testId}-image-mobile` : null}
                  alt={imageDesc ?? ''}
                />
              )}
              {videoId && (
                <promo-video-player
                  className="video-container Content"
                  playlist-id={videoId}
                ></promo-video-player>
              )}
              {hasMultiplePromoLinks && (
                <div
                  className="Promo-multiLinks-content u-flex u-flexCol u-paddingHoriz5gu u-marginBottom6gu u-marginTop6gu u-sm-marginTop0"
                  data-testid={testId ? `${testId}-multiLinks` : null}
                >
                  {promoLinks.map((link, index) => (
                    <Link
                      key={`${link.url || 'link'}-${index}`}
                      variant={LinkVariant.PRIMARY}
                      name={link.name}
                      url={link.url}
                      target={link.target}
                      analyticsName={link.analyticsName}
                      testId={testId ? `${testId}-button` : undefined}
                      className="Button--reversed"
                      ariaLabel={link.ariaLabel}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </Spacer>
  )
}
